import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Sparkles, X, Award, BarChart3, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import JoinCommunityModal from '@/components/JoinCommunityModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'dividrip_community_invite_dismissed_at';
const DISMISS_DAYS = 14; // re-show after 2 weeks if user dismissed

/**
 * JoinCommunityCard — Phase 1 Step 5
 *
 * Sits on the Dashboard for logged-in users who have NOT yet joined
 * the community. Self-hides for:
 *   • Non-logged-in users
 *   • Existing community members (user.community_handle set)
 *   • Users who dismissed it in the last 14 days
 *
 * Clicking "Join" opens <JoinCommunityModal />. On success, the card
 * removes itself.
 *
 * Violet palette to match Journal + CommunityTerms.
 */
export default function JoinCommunityCard({ onJoined }) {
  const { user, setAuthUser } = useAuth();
  const [communityState, setCommunityState] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const at = localStorage.getItem(DISMISS_KEY);
      if (!at) return false;
      const days = (Date.now() - Number(at)) / (1000 * 60 * 60 * 24);
      return days < DISMISS_DAYS;
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) { setCommunityState(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/community/me`, { withCredentials: true });
        if (!cancelled) setCommunityState(data);
      } catch {
        if (!cancelled) setCommunityState({ is_member: false });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  // Self-hide conditions
  if (!user) return null;
  if (dismissed) return null;
  if (!communityState) return null;
  if (communityState.is_member) return null;

  return (
    <>
      <div
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-3"
        data-testid="join-community-card"
      >
        <div className="relative rounded-2xl border-2 border-violet-300 dark:border-violet-700 bg-gradient-to-br from-violet-50 via-background to-background dark:from-violet-950/40 dark:via-background dark:to-background p-4 sm:p-5 overflow-hidden">
          <button
            type="button"
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 rounded-md"
            aria-label="Dismiss for 2 weeks"
            title="Dismiss for 2 weeks"
            data-testid="join-community-card-dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex items-start gap-3 sm:items-center flex-1 min-w-0">
              <div className="w-12 h-12 rounded-2xl bg-violet-100 dark:bg-violet-950/60 flex items-center justify-center shrink-0">
                <Users className="w-6 h-6 text-violet-600 dark:text-violet-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-foreground flex items-center gap-1.5">
                  Unlock the DiviDrip Community
                  <Sparkles className="w-4 h-4 text-violet-500 dark:text-violet-400" />
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Compare your strategy with thousands of verified dividend investors. Your specific dollars and share counts stay private.
                </p>
                <div className="flex flex-wrap gap-3 mt-2 text-[11px] sm:text-xs text-foreground/80">
                  <span className="inline-flex items-center gap-1">
                    <BarChart3 className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" />
                    Anonymous sector benchmarks
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Award className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" />
                    Earn passive-income milestone badges
                  </span>
                </div>
              </div>
            </div>
            <Button
              onClick={() => setOpen(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
              data-testid="join-community-card-cta"
            >
              Join the Community
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <JoinCommunityModal
        open={open}
        onClose={() => setOpen(false)}
        onJoined={(data) => {
          setCommunityState({ is_member: true, handle: data.handle });
          // Refresh auth user
          (async () => {
            try {
              const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
              if (setAuthUser) setAuthUser(me.data);
            } catch { /* ignore */ }
          })();
          if (onJoined) onJoined(data);
        }}
      />
    </>
  );
}
